import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import BasicLayout from '@/layouts/BasicLayout.vue'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    icon?: string
    skipPermissionCheck?: boolean
    menuKey?: string
    titleKey?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: BasicLayout,
    redirect: '/access-points',
    children: [
      {
        path: 'system-architecture',
        name: 'system-architecture',
        component: () => import('@/views/system-architecture/index.vue'),
        meta: { title: 'System Architecture', icon: 'apartment', skipPermissionCheck: true },
      },
      {
        path: 'home',
        name: 'home',
        component: () => import('@/views/home/index.vue'),
        meta: { title: 'Home', icon: 'home', skipPermissionCheck: true },
      },
      {
        path: 'scenes',
        name: 'scenes',
        component: () => import('@/views/scenes/index.vue'),
        meta: { title: 'Scenes', icon: 'appstore', skipPermissionCheck: true },
      },
      {
        path: 'access-points',
        name: 'access-points',
        component: () => import('@/views/access-points/index.vue'),
        meta: { title: 'Access Points', icon: 'gateway', skipPermissionCheck: true },
      },
      {
        path: 'scene-dicts',
        name: 'scene-dicts',
        component: () => import('@/views/scene-dicts/index.vue'),
        meta: { title: 'Scene Dicts', icon: 'book', skipPermissionCheck: true },
      },
      {
        path: 'merchant-thresholds',
        name: 'merchant-thresholds',
        component: () => import('@/views/merchant-thresholds/index.vue'),
        meta: { title: 'Merchant Thresholds', icon: 'shop', skipPermissionCheck: true },
      },
      {
        path: 'rules',
        name: 'rules',
        component: () => import('@/views/rules/index.vue'),
        meta: { title: 'Rules', icon: 'control', skipPermissionCheck: true },
      },
      {
        path: 'rules/create',
        name: 'rules-create',
        component: () => import('@/views/rules/form.vue'),
        meta: { menuKey: 'rules', titleKey: 'rules.create', skipPermissionCheck: true },
      },
      {
        path: 'rules/detail/:code',
        name: 'rules-detail',
        component: () => import('@/views/rules/detail.vue'),
        meta: { menuKey: 'rules', titleKey: 'rules.detail', skipPermissionCheck: true },
      },
      {
        path: 'rules/edit/:code',
        name: 'rules-edit',
        component: () => import('@/views/rules/form.vue'),
        meta: { menuKey: 'rules', titleKey: 'rules.edit', skipPermissionCheck: true },
      },
      {
        path: 'merchant-rules',
        name: 'merchant-rules',
        component: () => import('@/views/merchant-rules/index.vue'),
        meta: { title: 'Merchant Rules', icon: 'solution', skipPermissionCheck: true },
      },
      {
        path: 'merchant-rules/create',
        name: 'merchant-rules-create',
        component: () => import('@/views/merchant-rules/form.vue'),
        meta: { menuKey: 'merchant-rules', titleKey: 'merchantRules.create', skipPermissionCheck: true },
      },
      {
        path: 'merchant-rules/detail/:merchantNo/:ruleCode',
        name: 'merchant-rules-detail',
        component: () => import('@/views/merchant-rules/form.vue'),
        meta: { menuKey: 'merchant-rules', titleKey: 'merchantRules.detail', skipPermissionCheck: true },
      },
      {
        path: 'merchant-rules/edit/:merchantNo/:ruleCode',
        name: 'merchant-rules-edit',
        component: () => import('@/views/merchant-rules/form.vue'),
        meta: { menuKey: 'merchant-rules', titleKey: 'merchantRules.edit', skipPermissionCheck: true },
      },
      {
        path: 'strategies',
        name: 'strategies',
        component: () => import('@/views/strategies/index.vue'),
        meta: { title: 'Strategies', icon: 'deployment-unit', skipPermissionCheck: true },
      },
      {
        path: 'strategies/create',
        name: 'strategies-create',
        component: () => import('@/views/strategies/form.vue'),
        meta: { menuKey: 'strategies', titleKey: 'strategies.create', skipPermissionCheck: true },
      },
      {
        path: 'strategies/detail/:code',
        name: 'strategies-detail',
        component: () => import('@/views/strategies/detail.vue'),
        meta: { menuKey: 'strategies', titleKey: 'strategies.detail', skipPermissionCheck: true },
      },
      {
        path: 'strategies/edit/:code',
        name: 'strategies-edit',
        component: () => import('@/views/strategies/form.vue'),
        meta: { menuKey: 'strategies', titleKey: 'strategies.edit', skipPermissionCheck: true },
      },
      {
        path: 'features',
        name: 'features',
        component: () => import('@/views/features/index.vue'),
        meta: { title: 'Features', icon: 'function', skipPermissionCheck: true },
      },
      {
        path: 'feature-rates',
        name: 'feature-rates',
        component: () => import('@/views/feature-rates/index.vue'),
        meta: { title: 'Feature Rates', icon: 'line-chart', skipPermissionCheck: true },
      },
      {
        path: 'feature-rates/create',
        name: 'feature-rates-create',
        component: () => import('@/views/feature-rates/form.vue'),
        meta: { menuKey: 'feature-rates', titleKey: 'featureRates.create', skipPermissionCheck: true },
      },
      {
        path: 'feature-rates/detail/:code',
        name: 'feature-rates-detail',
        component: () => import('@/views/feature-rates/detail.vue'),
        meta: { menuKey: 'feature-rates', titleKey: 'featureRates.detail', skipPermissionCheck: true },
      },
      {
        path: 'feature-rates/edit/:code',
        name: 'feature-rates-edit',
        component: () => import('@/views/feature-rates/form.vue'),
        meta: { menuKey: 'feature-rates', titleKey: 'featureRates.edit', skipPermissionCheck: true },
      },
      {
        path: 'decision-trees',
        name: 'decision-trees',
        component: () => import('@/views/decision-trees/index.vue'),
        meta: { title: 'Decision Trees', icon: 'apartment', skipPermissionCheck: true },
      },
      {
        path: 'decision-trees/create',
        name: 'decision-trees-create',
        component: () => import('@/views/decision-trees/form.vue'),
        meta: { menuKey: 'decision-trees', titleKey: 'decisionTrees.create', skipPermissionCheck: true },
      },
      {
        path: 'decision-trees/detail/:code',
        name: 'decision-trees-detail',
        component: () => import('@/views/decision-trees/detail.vue'),
        meta: { menuKey: 'decision-trees', titleKey: 'decisionTrees.detail', skipPermissionCheck: true },
      },
      {
        path: 'decision-trees/edit/:code',
        name: 'decision-trees-edit',
        component: () => import('@/views/decision-trees/form.vue'),
        meta: { menuKey: 'decision-trees', titleKey: 'decisionTrees.edit', skipPermissionCheck: true },
      },
      {
        path: 'decision-publish',
        name: 'decision-publish',
        component: () => import('@/views/decision-publish/index.vue'),
        meta: { title: 'Decision Publish', icon: 'cloud-upload', skipPermissionCheck: true },
      },
      {
        path: 'verify/trace-single',
        name: 'verify-trace-single',
        component: () => import('@/views/verify/trace-single/index.vue'),
        meta: { menuKey: 'verify-trace-single', titleKey: 'verify.traceSingle', skipPermissionCheck: true },
      },
      {
        path: 'verify/execution-instances',
        name: 'verify-execution-instances',
        component: () => import('@/views/verify/execution-instances/index.vue'),
        meta: { menuKey: 'verify-execution-instances', titleKey: 'verify.executionInstances', skipPermissionCheck: true },
      },
      {
        path: 'verify/execution-instances/detail/:txnId',
        name: 'verify-execution-instance-detail',
        component: () => import('@/views/verify/trace-detail.vue'),
        meta: { menuKey: 'verify-execution-instances', titleKey: 'verify.executionDetail', skipPermissionCheck: true },
      },
      {
        path: 'verify/conclusions',
        name: 'verify-conclusions',
        component: () => import('@/views/verify/conclusions/index.vue'),
        meta: { menuKey: 'verify-conclusions', titleKey: 'verify.conclusions', skipPermissionCheck: true },
      },
      {
        path: 'verify/conclusions/detail/:txnId',
        name: 'verify-conclusion-detail',
        component: () => import('@/views/verify/trace-detail.vue'),
        meta: { menuKey: 'verify-conclusions', titleKey: 'verify.conclusionDetail', skipPermissionCheck: true },
      },
      {
        path: 'verify/simulate',
        name: 'verify-simulate',
        component: () => import('@/views/verify/simulate/index.vue'),
        meta: { menuKey: 'verify-simulate', titleKey: 'verify.simulate', skipPermissionCheck: true },
      },
      {
        path: 'verify/lineage',
        name: 'verify-lineage',
        component: () => import('@/views/verify/lineage/index.vue'),
        meta: { menuKey: 'verify-lineage', titleKey: 'verify.lineage', skipPermissionCheck: true },
      },
      {
        path: 'verify/trace-batch',
        name: 'verify-trace-batch',
        component: () => import('@/views/verify/coming-soon.vue'),
        meta: { menuKey: 'verify-trace-batch', titleKey: 'verify.traceBatch', skipPermissionCheck: true },
      },
      {
        path: 'verify/velocity-read',
        name: 'verify-velocity-read',
        component: () => import('@/views/verify/velocity-read/index.vue'),
        meta: { menuKey: 'verify-velocity-read', titleKey: 'verify.velocityRead', skipPermissionCheck: true },
      },
      {
        path: 'verify/velocity-write',
        name: 'verify-velocity-write',
        component: () => import('@/views/verify/coming-soon.vue'),
        meta: { menuKey: 'verify-velocity-write', titleKey: 'verify.velocityWrite', skipPermissionCheck: true },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

export default router
